import React, { useMemo } from 'react'
import { SubmitErrorHandler, useFormContext } from 'react-hook-form'
import { useEvent } from 'react-use-event-hook'
import { createPrivyNotAuthenticatedNotification } from '@components/Notifications/utils'
import { prepareGatedDataForSubmit } from '@components/Tokens/utils'
import { FancyButton } from '@ui'
import { parseUnits } from 'hooks/useBalance'
import { usePlatformMinMembershipPriceInEth } from 'hooks/usePlatformMinMembershipPriceInEth'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import {
    findDynamicPricingModule,
    findFixedPricingModule,
    TransactionStatus,
    useEditSpaceMembershipTransaction,
    useMembershipInfoWithPricingModule,
    usePricingModules,
    useRoleDetails,
} from 'use-towns-client'

import { EditMembershipSchemaType } from './editMembershipSchema'
import { checkIsDisabled, checkIsUnchanged } from './editMembershipSubmitHelpers'

export function EditMembershipSubmitButton({
    children,
    spaceId,
    transactionIsPending,
    pricingModules,
    minimumMembershipPrice,
    isLoadingPricingModules,
    isLoadingRoleDetails,
    roleDetails,
    currentPricingModule,
    isCurrentlyFree,
    isCurrentlyDynamic,
    onSuccess,
}: {
    children?: string
    spaceId: string | undefined
    transactionIsPending: boolean
    pricingModules: ReturnType<typeof usePricingModules>['data']
    minimumMembershipPrice: ReturnType<typeof usePlatformMinMembershipPriceInEth>['data']
    isLoadingPricingModules: boolean
    isLoadingRoleDetails: boolean
    roleDetails: ReturnType<typeof useRoleDetails>['roleDetails']
    currentPricingModule: ReturnType<typeof useMembershipInfoWithPricingModule>['data']
    isCurrentlyFree: boolean
    isCurrentlyDynamic: boolean
    onSuccess?: () => void
}) {
    const { handleSubmit, formState, watch, setError } = useFormContext<EditMembershipSchemaType>()
    const { defaultValues } = formState
    const watchAllFields = watch()
    // success and error statuses are handled by <BlockchainTxNotifier />
    const { editSpaceMembershipTransaction } = useEditSpaceMembershipTransaction()
    const { closePanel } = usePanelActions()

    const isUnchanged = useMemo(
        () => checkIsUnchanged(defaultValues, watchAllFields),
        [defaultValues, watchAllFields],
    )

    const isDisabled = checkIsDisabled({
        isSubmitting: formState.isSubmitting,
        isUnchanged,
        hasErrors: Object.keys(formState.errors).length > 0,
        gatingType: watchAllFields.gatingType,
        tokensGatedBy: watchAllFields.tokensGatedBy,
        usersGatedBy: watchAllFields.usersGatedBy,
        ethBalanceGatedBy: watchAllFields.ethBalanceGatedBy,
        isLoadingPricingModules,
        isLoadingRoleDetails,
        transactionIsPending,
    })

    const isSubmittedRef = React.useRef(false)

    const onValid = useEvent(async (data: EditMembershipSchemaType, getSigner: GetSigner) => {
        if (
            !spaceId ||
            !roleDetails ||
            transactionIsPending ||
            isLoadingPricingModules ||
            isSubmittedRef.current
        ) {
            return
        }

        //////////////////////////////////////////
        // check pricing
        //////////////////////////////////////////
        let priceInWei

        try {
            priceInWei = parseUnits(data.membershipCost)
        } catch (error) {
            setError('membershipCost', {
                type: 'manual',
                message: 'Please enter a valid eth value.',
            })
            return
        }

        const setPricingModuleError = () => {
            setError('membershipPricingType', {
                type: 'manual',
                message: 'Unable to get pricing modules. Please try again later',
            })
        }
        if (!pricingModules) {
            console.warn('No pricing modules found')
            setPricingModuleError()
            return
        }
        const dynamicPricingModule = findDynamicPricingModule(pricingModules)
        const fixedPricingModule = findFixedPricingModule(pricingModules)

        if (!dynamicPricingModule && !fixedPricingModule) {
            console.warn('Cannot find either dynamic pricing or fixed pricing modules')
            setPricingModuleError()
            return
        }

        const nextPricingModuleIsFixed = watchAllFields.membershipPricingType === 'fixed'
        const isSwitchingToFixedPriceFromDynamic = isCurrentlyDynamic && nextPricingModuleIsFixed

        // when switching to fixed price from dynamic, or if the fixed price space has a non-zero price,
        // the updated price must be at least the minimum price
        // or else the contract will revert
        if (
            (isSwitchingToFixedPriceFromDynamic || !isCurrentlyFree) &&
            minimumMembershipPrice !== undefined &&
            nextPricingModuleIsFixed &&
            priceInWei < parseUnits(minimumMembershipPrice)
        ) {
            setError('membershipCost', {
                type: 'manual',
                message: `Fixed price must be at least ${minimumMembershipPrice} ETH`,
            })
            return
        }

        let pricingModuleToSubmit: string
        if (nextPricingModuleIsFixed && fixedPricingModule) {
            pricingModuleToSubmit = await fixedPricingModule.module
        }
        // dynamic priced spaces cannot currently be updated to a different module, so we just submit the current module
        // if this changes in the future, the editMembership user operation will also need to handle this
        else if (dynamicPricingModule && currentPricingModule?.address) {
            pricingModuleToSubmit = currentPricingModule.address
        } else {
            console.warn('No pricing module to submit')
            setPricingModuleError()
            return
        }

        const { usersGatedBy, ruleData } = prepareGatedDataForSubmit(
            data.gatingType,
            data.tokensGatedBy,
            data.usersGatedBy,
            data.ethBalanceGatedBy,
        )

        isSubmittedRef.current = true

        const signer = await getSigner()

        if (!signer) {
            createPrivyNotAuthenticatedNotification()
            isSubmittedRef.current = false
            return
        }

        const txContext = await editSpaceMembershipTransaction({
            spaceId,
            updateRoleParams: {
                roleId: 1,
                roleName: roleDetails.name,
                permissions: roleDetails.permissions,
                spaceNetworkId: spaceId,
                users: usersGatedBy,
                ruleData,
            },
            membershipParams: {
                membershipPrice: priceInWei,
                membershipSupply: data.membershipLimit,
                pricingModule: pricingModuleToSubmit,
            },
            signer,
        })

        isSubmittedRef.current = false

        if (txContext?.status === TransactionStatus.Success) {
            closePanel()
            onSuccess?.()
        }
    })

    const onInvalid: SubmitErrorHandler<EditMembershipSchemaType> = useEvent((errors) => {
        console.error(errors)
    })

    return (
        <WalletReady>
            {({ getSigner }) => (
                <FancyButton
                    cta
                    data-testid="submit-button"
                    disabled={isDisabled}
                    style={{
                        pointerEvents: isDisabled ? 'none' : 'initial',
                    }}
                    onClick={handleSubmit((data) => onValid(data, getSigner), onInvalid)}
                >
                    {children}
                </FancyButton>
            )}
        </WalletReady>
    )
}
